/** @module @liquidefy/code-editor/document/document-edit @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  Position,
  Range,
  EditorResult,
  EditOperation,
  EditOperationKind,
  EditOrigin,
  DocumentState,
} from '../code-editor-types.js';

import {
  ok,
  err,
  positionToOffset,
  offsetToPosition,
  comparePositions,
  rangesOverlap,
  rangeIsEmpty,
  lineCount,
} from '../code-editor.js';

// ═══ §2 EDIT CONSTRUCTION — build typed edit operations ═══

export function createInsertEdit(
  position: Position,
  text: string,
  origin: EditOrigin = 'user',
): EditOperation {
  return {
    kind: 'insert',
    range: { start: position, end: position },
    text,
    origin,
  };
}

export function createDeleteEdit(
  range: Range,
  origin: EditOrigin = 'user',
): EditOperation {
  return {
    kind: 'delete',
    range,
    text: '',
    origin,
  };
}

export function createReplaceEdit(
  range: Range,
  text: string,
  origin: EditOrigin = 'user',
): EditOperation {
  return {
    kind: 'replace',
    range,
    text,
    origin,
  };
}

export function createTransposeEdit(
  range: Range,
  origin: EditOrigin = 'user',
): EditOperation {
  return {
    kind: 'transpose',
    range,
    text: '',
    origin,
  };
}

export function createIndentEdit(
  lines: number[],
  tabSize: number,
  origin: EditOrigin = 'user',
): EditOperation {
  const startLine = Math.min(...lines);
  const endLine = Math.max(...lines);
  return {
    kind: 'indent',
    range: {
      start: { line: startLine, column: 0 },
      end: { line: endLine, column: 0 },
    },
    text: ' '.repeat(tabSize),
    origin,
  };
}

export function createDedentEdit(
  lines: number[],
  tabSize: number,
  content: string,
  origin: EditOrigin = 'user',
): EditOperation {
  const startLine = Math.min(...lines);
  const endLine = Math.max(...lines);
  const allLines = content.split(/\r\n|\r|\n/);
  let maxDedent = tabSize;

  for (const lineNum of lines) {
    if (lineNum >= 0 && lineNum < allLines.length) {
      const line = allLines[lineNum]!;
      let spaces = 0;
      for (let i = 0; i < line.length && spaces < tabSize; i++) {
        if (line[i] === ' ') spaces++;
        else if (line[i] === '\t') { spaces = tabSize; break; }
        else break;
      }
      maxDedent = Math.min(maxDedent, spaces);
    }
  }

  return {
    kind: 'dedent',
    range: {
      start: { line: startLine, column: 0 },
      end: { line: endLine, column: 0 },
    },
    text: String(maxDedent),
    origin,
  };
}

export function createCommentToggleEdit(
  lines: number[],
  commentRule: { lineComment?: string; blockComment?: readonly [string, string] },
  content: string,
  origin: EditOrigin = 'user',
): EditOperation {
  const startLine = Math.min(...lines);
  const endLine = Math.max(...lines);
  const marker = commentRule.lineComment ?? '';
  const allLines = content.split(/\r\n|\r|\n/);

  const allCommented = lines.every(l => {
    if (l < 0 || l >= allLines.length) return false;
    return allLines[l]!.trimStart().startsWith(marker);
  });

  return {
    kind: 'commentToggle',
    range: {
      start: { line: startLine, column: 0 },
      end: { line: endLine, column: 0 },
    },
    text: allCommented ? '' : marker,
    origin,
  };
}

export function createBracketCompleteEdit(
  position: Position,
  open: string,
  close: string,
  origin: EditOrigin = 'auto-close',
): EditOperation {
  return {
    kind: 'bracketComplete',
    range: { start: position, end: position },
    text: open + close,
    origin,
  };
}

// ═══ §3 EDIT VALIDATION — check edits against document state ═══

export function validateEdit(edit: EditOperation, state: DocumentState): EditorResult<boolean> {
  if (state.isReadOnly) return err('READONLY', 'Document is read-only');

  const rangeResult = validateEditRange(edit.range, state);
  if (!rangeResult.success) return rangeResult;

  if (edit.kind === 'insert' && edit.text.length === 0) {
    return err('EMPTY_INSERT', 'Insert edit has no text');
  }

  if (edit.kind === 'delete' && rangeIsEmpty(edit.range)) {
    return err('EMPTY_DELETE', 'Delete edit has empty range');
  }

  return ok(true, 'VALID', 'Edit is valid');
}

export function validateEditRange(range: Range, state: DocumentState): EditorResult<boolean> {
  const totalLines = lineCount(state.content);
  const lines = state.content.split(/\r\n|\r|\n/);

  if (range.start.line < 0 || range.start.line >= totalLines) {
    return err('OUT_OF_BOUNDS', `Start line ${range.start.line} out of bounds (0..${totalLines - 1})`);
  }
  if (range.end.line < 0 || range.end.line >= totalLines) {
    return err('OUT_OF_BOUNDS', `End line ${range.end.line} out of bounds (0..${totalLines - 1})`);
  }
  if (range.start.column < 0 || range.start.column > lines[range.start.line]!.length) {
    return err('OUT_OF_BOUNDS', `Start column ${range.start.column} out of bounds`);
  }
  if (range.end.column < 0 || range.end.column > lines[range.end.line]!.length) {
    return err('OUT_OF_BOUNDS', `End column ${range.end.column} out of bounds`);
  }
  if (comparePositions(range.start, range.end) > 0) {
    return err('INVALID_RANGE', 'Range start is after range end');
  }

  return ok(true, 'VALID', 'Range is valid');
}

export function isEditNoOp(edit: EditOperation): boolean {
  if (edit.kind === 'insert' && edit.text.length === 0) return true;
  if (edit.kind === 'delete' && rangeIsEmpty(edit.range)) return true;
  if (edit.kind === 'replace' && rangeIsEmpty(edit.range) && edit.text.length === 0) return true;
  return false;
}

export function editsOverlap(a: EditOperation, b: EditOperation): boolean {
  return rangesOverlap(a.range, b.range);
}

// ═══ §4 EDIT TRANSFORMATION — OT-style shift, compose, invert, normalize ═══

export function transformEdit(edit: EditOperation, against: EditOperation): EditOperation {
  const againstStartOffset = positionToOffsetFromRange(against.range.start);
  const againstEndOffset = positionToOffsetFromRange(against.range.end);
  const delta = computeEditDelta(against);

  const editStartOffset = positionToOffsetFromRange(edit.range.start);
  const editEndOffset = positionToOffsetFromRange(edit.range.end);

  if (editStartOffset >= againstEndOffset) {
    const newStart = shiftPosition(edit.range.start, against, delta);
    const newEnd = shiftPosition(edit.range.end, against, delta);
    return { ...edit, range: { start: newStart, end: newEnd } };
  }

  if (editEndOffset <= againstStartOffset) {
    return edit;
  }

  return edit;
}

export function composeEdits(edits: EditOperation[]): EditOperation[] {
  if (edits.length <= 1) return edits;

  const sorted = [...edits].sort((a, b) => comparePositions(a.range.start, b.range.start));
  const composed: EditOperation[] = [];

  for (const edit of sorted) {
    if (isEditNoOp(edit)) continue;

    const last = composed.length > 0 ? composed[composed.length - 1] : null;

    if (
      last &&
      last.kind === 'insert' &&
      edit.kind === 'insert' &&
      last.range.start.line === edit.range.start.line &&
      last.range.start.column + last.text.length === edit.range.start.column &&
      last.origin === edit.origin
    ) {
      composed[composed.length - 1] = {
        ...last,
        text: last.text + edit.text,
      };
      continue;
    }

    if (
      last &&
      last.kind === 'delete' &&
      edit.kind === 'delete' &&
      comparePositions(last.range.end, edit.range.start) === 0 &&
      last.origin === edit.origin
    ) {
      composed[composed.length - 1] = {
        ...last,
        range: { start: last.range.start, end: edit.range.end },
      };
      continue;
    }

    composed.push(edit);
  }

  return composed;
}

export function invertEdit(edit: EditOperation, originalContent: string): EditOperation {
  const startOffset = positionToOffset(originalContent, edit.range.start);
  const endOffset = positionToOffset(originalContent, edit.range.end);

  switch (edit.kind) {
    case 'insert': {
      const insertedLines = edit.text.split(/\r\n|\r|\n/);
      let endPos: Position;
      if (insertedLines.length === 1) {
        endPos = { line: edit.range.start.line, column: edit.range.start.column + edit.text.length };
      } else {
        endPos = {
          line: edit.range.start.line + insertedLines.length - 1,
          column: insertedLines[insertedLines.length - 1]!.length,
        };
      }
      return {
        kind: 'delete',
        range: { start: edit.range.start, end: endPos },
        text: '',
        origin: edit.origin === 'undo' ? 'redo' : 'undo',
      };
    }

    case 'delete': {
      const deletedText = originalContent.slice(startOffset, endOffset);
      return {
        kind: 'insert',
        range: { start: edit.range.start, end: edit.range.start },
        text: deletedText,
        origin: edit.origin === 'undo' ? 'redo' : 'undo',
      };
    }

    case 'replace': {
      const replacedText = originalContent.slice(startOffset, endOffset);
      const insertedLines = edit.text.split(/\r\n|\r|\n/);
      let replaceEnd: Position;
      if (insertedLines.length === 1) {
        replaceEnd = { line: edit.range.start.line, column: edit.range.start.column + edit.text.length };
      } else {
        replaceEnd = {
          line: edit.range.start.line + insertedLines.length - 1,
          column: insertedLines[insertedLines.length - 1]!.length,
        };
      }
      return {
        kind: 'replace',
        range: { start: edit.range.start, end: replaceEnd },
        text: replacedText,
        origin: edit.origin === 'undo' ? 'redo' : 'undo',
      };
    }

    default:
      return {
        ...edit,
        origin: edit.origin === 'undo' ? 'redo' : 'undo',
      };
  }
}

export function normalizeEdits(edits: EditOperation[]): EditOperation[] {
  const filtered = edits.filter(e => !isEditNoOp(e));
  const sorted = filtered.sort((a, b) => comparePositions(a.range.start, b.range.start));
  return composeEdits(sorted);
}

// ═══ §5 EDIT APPLICATION — apply edits to content strings ═══

export function applyEditToContent(content: string, edit: EditOperation): string {
  const startOffset = positionToOffset(content, edit.range.start);
  const endOffset = positionToOffset(content, edit.range.end);

  switch (edit.kind) {
    case 'insert':
      return content.slice(0, startOffset) + edit.text + content.slice(startOffset);

    case 'delete':
      return content.slice(0, startOffset) + content.slice(endOffset);

    case 'replace':
      return content.slice(0, startOffset) + edit.text + content.slice(endOffset);

    case 'transpose': {
      if (startOffset <= 0 || startOffset >= content.length) return content;
      const chars = content.split('');
      const temp = chars[startOffset - 1]!;
      chars[startOffset - 1] = chars[startOffset]!;
      chars[startOffset] = temp;
      return chars.join('');
    }

    case 'bracketComplete':
      return content.slice(0, startOffset) + edit.text + content.slice(startOffset);

    default:
      return content;
  }
}

export function applyEditsToContent(content: string, edits: EditOperation[]): string {
  const sorted = [...edits].sort((a, b) => comparePositions(b.range.start, a.range.start));
  let result = content;
  for (const edit of sorted) {
    result = applyEditToContent(result, edit);
  }
  return result;
}

export function computeEditDelta(edit: EditOperation): number {
  switch (edit.kind) {
    case 'insert':
    case 'bracketComplete':
      return edit.text.length;

    case 'delete': {
      const lines = linesInRange(edit.range);
      const startCol = edit.range.start.column;
      const endCol = edit.range.end.column;
      if (lines === 0) return -(endCol - startCol);
      return -(endCol + startCol + lines);
    }

    case 'replace': {
      const lines = linesInRange(edit.range);
      const startCol = edit.range.start.column;
      const endCol = edit.range.end.column;
      const deletedChars = lines === 0 ? (endCol - startCol) : (endCol + startCol + lines);
      return edit.text.length - deletedChars;
    }

    case 'transpose':
      return 0;

    default:
      return 0;
  }
}

export function computeNewPosition(position: Position, edit: EditOperation): Position {
  const editStart = edit.range.start;
  const editEnd = edit.range.end;

  if (comparePositions(position, editStart) <= 0) {
    return position;
  }

  switch (edit.kind) {
    case 'insert': {
      const insertedLines = edit.text.split(/\r\n|\r|\n/);
      const insertedLineCount = insertedLines.length - 1;

      if (position.line === editStart.line) {
        if (insertedLineCount === 0) {
          return { line: position.line, column: position.column + edit.text.length };
        }
        return {
          line: position.line + insertedLineCount,
          column: insertedLines[insertedLineCount]!.length + (position.column - editStart.column),
        };
      }
      return { line: position.line + insertedLineCount, column: position.column };
    }

    case 'delete': {
      if (comparePositions(position, editEnd) <= 0) {
        return editStart;
      }
      const deletedLines = editEnd.line - editStart.line;
      if (position.line === editEnd.line) {
        return {
          line: editStart.line,
          column: editStart.column + (position.column - editEnd.column),
        };
      }
      return { line: position.line - deletedLines, column: position.column };
    }

    case 'replace': {
      if (comparePositions(position, editEnd) <= 0) {
        const insertedLines = edit.text.split(/\r\n|\r|\n/);
        const lastLine = insertedLines[insertedLines.length - 1];
        return {
          line: editStart.line + insertedLines.length - 1,
          column: insertedLines.length === 1
            ? editStart.column + lastLine!.length
            : lastLine!.length,
        };
      }
      const deletedLines = editEnd.line - editStart.line;
      const insertedLines = edit.text.split(/\r\n|\r|\n/);
      const insertedLineCount = insertedLines.length - 1;
      const lineDelta = insertedLineCount - deletedLines;

      if (position.line === editEnd.line) {
        const lastInserted = insertedLines[insertedLines.length - 1];
        return {
          line: position.line + lineDelta,
          column: insertedLineCount === 0 && deletedLines === 0
            ? position.column + edit.text.length - (editEnd.column - editStart.column)
            : lastInserted!.length + (position.column - editEnd.column),
        };
      }
      return { line: position.line + lineDelta, column: position.column };
    }

    default:
      return position;
  }
}

// ═══ §6 EDIT ANALYSIS — what does this edit affect? ═══

export function editAffectsRange(edit: EditOperation, range: Range): boolean {
  return rangesOverlap(edit.range, range);
}

export function editAffectsLine(edit: EditOperation, lineNumber: number): boolean {
  return lineNumber >= edit.range.start.line && lineNumber <= edit.range.end.line;
}

export function editCrossesLines(edit: EditOperation): boolean {
  if (edit.range.start.line !== edit.range.end.line) return true;
  if (edit.kind === 'insert' || edit.kind === 'replace' || edit.kind === 'bracketComplete') {
    return edit.text.includes('\n') || edit.text.includes('\r');
  }
  return false;
}

export function classifyEdit(edit: EditOperation): 'simple-insert' | 'simple-delete' | 'multiline' | 'block' {
  if (editCrossesLines(edit)) {
    if (edit.kind === 'indent' || edit.kind === 'dedent' || edit.kind === 'commentToggle') {
      return 'block';
    }
    return 'multiline';
  }

  if (edit.kind === 'insert' || edit.kind === 'bracketComplete') return 'simple-insert';
  if (edit.kind === 'delete') return 'simple-delete';
  if (edit.kind === 'indent' || edit.kind === 'dedent' || edit.kind === 'commentToggle') return 'block';

  return 'simple-insert';
}

// ═══ INTERNAL HELPERS ═══

function positionToOffsetFromRange(pos: Position): number {
  return pos.line * 10000 + pos.column;
}

function linesInRange(range: Range): number {
  return range.end.line - range.start.line;
}

function shiftPosition(pos: Position, against: EditOperation, delta: number): Position {
  const againstEnd = against.range.end;
  const againstStart = against.range.start;

  if (against.kind === 'insert' || against.kind === 'bracketComplete') {
    const insertedLines = against.text.split(/\r\n|\r|\n/);
    const insertedLineCount = insertedLines.length - 1;

    if (pos.line === againstStart.line && pos.column >= againstStart.column) {
      if (insertedLineCount === 0) {
        return { line: pos.line, column: pos.column + against.text.length };
      }
      return {
        line: pos.line + insertedLineCount,
        column: insertedLines[insertedLineCount]!.length + (pos.column - againstStart.column),
      };
    }
    if (pos.line > againstStart.line) {
      return { line: pos.line + insertedLineCount, column: pos.column };
    }
    return pos;
  }

  if (against.kind === 'delete') {
    const deletedLines = againstEnd.line - againstStart.line;
    if (comparePositions(pos, againstEnd) >= 0) {
      if (pos.line === againstEnd.line) {
        return {
          line: againstStart.line,
          column: againstStart.column + (pos.column - againstEnd.column),
        };
      }
      return { line: pos.line - deletedLines, column: pos.column };
    }
    if (comparePositions(pos, againstStart) > 0) {
      return againstStart;
    }
    return pos;
  }

  if (against.kind === 'replace') {
    const deletedLines = againstEnd.line - againstStart.line;
    const insertedLines = against.text.split(/\r\n|\r|\n/);
    const insertedLineCount = insertedLines.length - 1;
    const lineDelta = insertedLineCount - deletedLines;

    if (comparePositions(pos, againstEnd) >= 0) {
      if (pos.line === againstEnd.line) {
        const lastInserted = insertedLines[insertedLines.length - 1];
        return {
          line: pos.line + lineDelta,
          column: lastInserted!.length + (pos.column - againstEnd.column),
        };
      }
      return { line: pos.line + lineDelta, column: pos.column };
    }
    if (comparePositions(pos, againstStart) > 0) {
      const lastInserted = insertedLines[insertedLines.length - 1];
      return {
        line: againstStart.line + insertedLineCount,
        column: insertedLineCount === 0
          ? againstStart.column + lastInserted!.length
          : lastInserted!.length,
      };
    }
    return pos;
  }

  return pos;
}
